import { useEffect } from "react";
import { useDrop } from "react-dnd";
import { useDispatch, useSelector } from "react-redux";
import { ItemTypes } from "../../../abstract/controller/enum/dnd/ItemTypes";
import * as nodeUtils from "../../../abstract/controller/utils/nodeUtils";
import useNodeParent from "../../../abstract/hook/useNodeParent";
import {
  getStatusPositioned,
  setDrag,
  setIdle,
  setPosition,
} from "../../../abstract/redux/slice/statusSlice";
import { handleChoiceMove } from "../../../abstract/redux/slice/structureSlice";
import { DragOverlay } from "../../ui/overlay/DragOverlay";
import styles from "./ChoicePanel.module.scss";
import { showChoiceDrop } from "./logic/showChoiceDrop";

function ChoicePanel({ node, forceIsTop = false }) {
  const _dispatch = useDispatch();
  const _isStatusPositioned = useSelector(getStatusPositioned);

  const _id = nodeUtils.getID(node);
  const _nodeParent = useNodeParent(_id);

  //---------------------------------------------------------------------

  const [{ _isOver, _canDrop }, drop] = useDrop(
    () => ({
      accept: ItemTypes.CHOICE,
      collect: (monitor) => ({
        _isOver: monitor.isOver(),
        _canDrop: monitor.canDrop(),
      }),
      canDrop(item) {
        return showChoiceDrop(item.element, node, _nodeParent, forceIsTop);
      },
      drop(item) {
        _dispatch(setIdle());
        _dispatch(handleChoiceMove(nodeUtils.getID(item.element), _id, forceIsTop));
      },
    }),
    [_id, node, _nodeParent, forceIsTop]
  );

  useEffect(() => {
    if (!_canDrop) {
      return;
    }

    if (_isOver) {
      _dispatch(setPosition({ hidden: true }));
    } else {
      _dispatch(setDrag({ fromPosition: true }));
    }
  }, [_isOver, _canDrop, _dispatch]);

  const handleOnMouseEnter = (e) => {
    e.stopPropagation();
  };

  const handleOnMouseLeave = (e) => {
    e.stopPropagation();
  };

  //---------------------------------------------------------------------

  const _isNodeOver = _isStatusPositioned && _isOver;

  const getClass = () => {
    return `${styles["panel"]} ${_isNodeOver ? styles["panel-extended"] : ""}`;
  };

  return (
    <div
      ref={drop}
      className={getClass()}
      onMouseOver={handleOnMouseEnter}
      onMouseOut={handleOnMouseLeave}>
      <DragOverlay type={ItemTypes.CHOICE} isVisible={_isNodeOver} />
    </div>
  );
}

export default ChoicePanel;
